import { useState, useMemo } from 'react';
import { Gift, Plus, Trash2, Pencil, Power, X, Package, Check } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { Product } from '@/types';
import { useProducts, useCreateProduct, useUpdateProduct, useDeleteProduct } from '@/hooks/useProducts';
import { ComboItemPayload } from '@/services/products';
import { formatPrice, parseARNumber, formatARNumber } from '@/utils/format';
import { Unit, toBase, UNIT_LABEL_SHORT } from '@/utils/units';
import CurrencyInput from '@/components/ui/CurrencyInput';

// Fila del armador de componentes (qué incluye el combo)
interface ComponentRow {
  component_product_id?: number;
  component_name?: string;
  quantity: number;
}

// Costo final por unidad base: base*(1+iva%) + flete + impuestos + comisiones
function landedCost(p?: Product): number {
  if (!p || p.cost_price == null) return 0;
  const base = Number(p.cost_price) || 0;
  const ivaPct = p.iva_rate != null ? Number(p.iva_rate) : 0;
  const freight = Number(p.freight_cost) || 0;
  const tax = Number(p.tax_cost) || 0;
  const commission = Number(p.commission_cost) || 0;
  return base * (1 + ivaPct / 100) + freight + tax + commission;
}

interface ComboForm {
  name: string;
  code: string;
  description: string;
  price_cash: string;  // precio único del combo
  stock: string;
  min_stock: string;
  track_stock: boolean;
  components: ComponentRow[];
}

const emptyForm: ComboForm = {
  name: '',
  code: '',
  description: '',
  price_cash: '',
  stock: '0',
  min_stock: '0',
  track_stock: true,
  components: [{ quantity: 1 }],
};

export default function Combos() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState<ComboForm>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<Product | null>(null);

  // Combos (activos e inactivos para poder reactivar)
  const { data: combos = [], isLoading } = useProducts({ only_active: false, is_combo: true });
  // Productos normales para elegir componentes
  const { data: products = [] } = useProducts({ only_active: true, is_combo: false });

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const productOptions = useMemo(
    () => [...products].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [products]
  );

  const productById = useMemo(() => {
    const m = new Map<number, Product>();
    products.forEach((p) => m.set(p.id, p));
    return m;
  }, [products]);

  // Costo del combo = suma del costo final de cada producto incluido × cantidad.
  // landedCost está por unidad base (gramo para peso); convertimos la cantidad
  // ingresada (en la unidad de display del producto) a la base antes de multiplicar.
  const comboCost = useMemo(() => {
    return form.components.reduce((sum, c) => {
      if (!c.component_product_id) return sum;
      const p = productById.get(c.component_product_id);
      if (!p) return sum;
      const qtyBase = toBase(Number(c.quantity) || 0, (p.display_unit as Unit) || 'unit');
      return sum + landedCost(p) * qtyBase;
    }, 0);
  }, [form.components, productById]);

  // Precio "normal" del combo = suma del precio de venta (efectivo) de cada producto incluido.
  // Sirve para comparar contra el precio promo y mostrar el ahorro.
  const comboNormalPrice = useMemo(() => {
    return form.components.reduce((sum, c) => {
      if (!c.component_product_id) return sum;
      const p = productById.get(c.component_product_id);
      if (!p || p.sale_price == null) return sum;
      const qtyBase = toBase(Number(c.quantity) || 0, (p.display_unit as Unit) || 'unit');
      return sum + Number(p.sale_price) * qtyBase;
    }, 0);
  }, [form.components, productById]);

  // ¿Algún componente sin costo cargado? (para avisar que el costo es parcial)
  const hasComponentWithoutCost = useMemo(
    () =>
      form.components.some(
        (c) => c.component_product_id && landedCost(productById.get(c.component_product_id)) <= 0
      ),
    [form.components, productById]
  );

  // Margen/ganancia según el precio efectivo ingresado
  const cashPriceNum = parseARNumber(form.price_cash) ?? 0;
  const comboProfit = cashPriceNum - comboCost;
  const comboMarginPct = cashPriceNum > 0 ? (comboProfit / cashPriceNum) * 100 : 0;
  const comboMarkupPct = comboCost > 0 ? (comboProfit / comboCost) * 100 : 0;

  // Ahorro del combo vs comprar los productos por separado
  const comboSaving = comboNormalPrice - cashPriceNum;
  const comboSavingPct = comboNormalPrice > 0 ? (comboSaving / comboNormalPrice) * 100 : 0;

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = (combo: Product) => {
    setEditing(combo);
    setForm({
      name: combo.name,
      code: combo.code ?? '',
      description: combo.description ?? '',
      price_cash: combo.sale_price != null ? formatARNumber(Number(combo.sale_price)) : '',
      stock: String(combo.current_stock ?? 0),
      min_stock: String(combo.minimum_stock ?? 0),
      track_stock: combo.track_stock,
      components: (combo.combo_items && combo.combo_items.length > 0)
        ? combo.combo_items.map((ci) => ({
            component_product_id: ci.component_product_id,
            component_name: ci.component_name,
            quantity: Number(ci.quantity) || 1,
          }))
        : [{ quantity: 1 }],
    });
    setIsModalOpen(true);
  };

  const updateForm = (patch: Partial<ComboForm>) => setForm((f) => ({ ...f, ...patch }));

  const updateComponent = (idx: number, patch: Partial<ComponentRow>) => {
    setForm((f) => ({
      ...f,
      components: f.components.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  };

  const addComponentRow = () =>
    setForm((f) => ({ ...f, components: [...f.components, { quantity: 1 }] }));

  const removeComponentRow = (idx: number) =>
    setForm((f) => ({ ...f, components: f.components.filter((_, i) => i !== idx) }));

  const isValid = form.name.trim().length > 0 && (parseARNumber(form.price_cash) ?? 0) > 0;

  const handleSave = async () => {
    if (!isValid) return;

    const cash = parseARNumber(form.price_cash) ?? 0;
    const combo_items: ComboItemPayload[] = form.components
      .filter((c) => c.component_product_id || (c.component_name && c.component_name.trim()))
      .map((c) => ({
        component_product_id: c.component_product_id ?? null,
        component_name: c.component_product_id ? null : (c.component_name?.trim() || null),
        quantity: Number(c.quantity) || 1,
      }));

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      description: form.description.trim() || undefined,
      is_combo: true,
      // Costo automático desde los componentes: el backend lo recalcula y lo mantiene
      // actualizado si cambia el costo de un componente (ej. el granel padre).
      cost_from_components: true,
      dimension: 'count' as const,
      display_unit: 'unit' as const,
      // Costo calculado a partir de los productos incluidos (para margen/reportes)
      cost_price: comboCost > 0 ? comboCost : undefined,
      // Precio único del combo (el backend sincroniza las columnas por método)
      sale_price: cash,
      current_stock: parseFloat(form.stock) || 0,
      minimum_stock: parseFloat(form.min_stock) || 0,
      track_stock: form.track_stock,
      combo_items,
    };

    try {
      if (editing) {
        await updateProduct.mutateAsync({ id: editing.id, data: payload });
      } else {
        await createProduct.mutateAsync(payload);
      }
      setIsModalOpen(false);
      setForm(emptyForm);
      setEditing(null);
    } catch (e) {
      console.error('Error guardando combo:', e);
    }
  };

  const toggleActive = async (combo: Product) => {
    await updateProduct.mutateAsync({ id: combo.id, data: { is_active: !combo.is_active } });
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteProduct.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (e) {
      console.error('Error eliminando combo:', e);
    }
  };

  const componentLabel = (ci: NonNullable<Product['combo_items']>[number]) => {
    const qty = Number(ci.quantity) || 0;
    const unit = ci.component_display_unit as Unit | undefined;
    // Producto por peso: mostrar con unidad (ej. "0,25 kg Harina"); por unidad: "2x Galleta"
    if (unit && unit !== 'unit') {
      return `${qty} ${UNIT_LABEL_SHORT[unit]} ${ci.display_name ?? 'Ítem'}`;
    }
    return `${qty > 1 ? `${qty}x ` : ''}${ci.display_name ?? 'Ítem'}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-taupe-800 flex items-center gap-2">
            <Gift className="h-6 w-6 text-purple-600" />
            Combos / Promociones
          </h1>
          <p className="text-taupe-500">Armá combos con precio promocional propio</p>
        </div>
        <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="h-5 w-5 mr-2" />
          Nuevo combo
        </Button>
      </div>

      {/* Lista de combos */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-taupe-600 mx-auto"></div>
        </div>
      ) : combos.length === 0 ? (
        <div className="bg-white rounded-2xl border border-cream-200 text-center py-16">
          <Gift className="h-16 w-16 text-taupe-300 mx-auto mb-4" />
          <p className="text-taupe-600 font-medium">No hay combos creados</p>
          <p className="text-taupe-400 text-sm">Creá tu primer combo de promoción</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {combos.map((combo) => (
            <div
              key={combo.id}
              className={`bg-white rounded-2xl border p-5 flex flex-col ${
                combo.is_active ? 'border-cream-200' : 'border-cream-200 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                    COMBO
                  </span>
                  <h3 className="font-semibold text-taupe-800 truncate" title={combo.name}>
                    {combo.name}
                  </h3>
                </div>
                {!combo.is_active && (
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full flex-shrink-0">
                    Inactivo
                  </span>
                )}
              </div>

              {/* Precio del combo */}
              <p className="text-2xl font-bold text-green-600 mb-3">
                {formatPrice(combo.sale_price ?? 0)}
              </p>

              {/* Contenido */}
              {combo.combo_items && combo.combo_items.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs uppercase tracking-wide text-taupe-400 mb-1">Incluye</p>
                  <ul className="text-sm text-taupe-600 space-y-0.5">
                    {combo.combo_items.map((ci) => (
                      <li key={ci.id} className="flex items-center gap-1">
                        <Package className="h-3.5 w-3.5 text-taupe-400" />
                        {componentLabel(ci)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Stock */}
              <p className="text-sm text-taupe-500 mb-4">
                {combo.track_stock ? `Stock: ${Number(combo.current_stock ?? 0)}` : 'Sin control de stock'}
              </p>

              {/* Acciones */}
              <div className="mt-auto flex items-center gap-2">
                <button
                  onClick={() => openEdit(combo)}
                  className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-cream-100 hover:bg-cream-200 text-taupe-700 text-sm font-medium"
                >
                  <Pencil className="h-4 w-4" />
                  Editar
                </button>
                <button
                  onClick={() => toggleActive(combo)}
                  title={combo.is_active ? 'Desactivar' : 'Activar'}
                  className={`p-2 rounded-lg text-sm ${
                    combo.is_active
                      ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                      : 'bg-green-50 text-green-600 hover:bg-green-100'
                  }`}
                >
                  <Power className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDeleteConfirm(combo)}
                  title="Eliminar"
                  className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal crear/editar */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Editar combo' : 'Nuevo combo'}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Nombre del combo *"
            placeholder="Nombre del combo"
            value={form.name}
            onChange={(e) => updateForm({ name: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Código (opcional)"
              placeholder="Ej: CMB-01"
              value={form.code}
              onChange={(e) => updateForm({ code: e.target.value })}
            />
            <Input
              label="Descripción (opcional)"
              placeholder="Detalle"
              value={form.description}
              onChange={(e) => updateForm({ description: e.target.value })}
            />
          </div>

          {/* Componentes */}
          <div>
            <label className="block text-sm font-medium text-taupe-700 mb-1.5">¿Qué incluye?</label>
            <div className="space-y-2">
              {form.components.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    value={c.component_product_id ?? ''}
                    onChange={(e) =>
                      updateComponent(idx, {
                        component_product_id: e.target.value ? parseInt(e.target.value) : undefined,
                        component_name: e.target.value ? undefined : c.component_name,
                      })
                    }
                    className="flex-1 px-3 py-2 border border-cream-300 rounded-xl text-sm bg-white focus:outline-none focus:border-taupe-400"
                  >
                    <option value="">— Texto libre —</option>
                    {productOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  {!c.component_product_id && (
                    <input
                      type="text"
                      placeholder="Nombre del ítem"
                      value={c.component_name ?? ''}
                      onChange={(e) => updateComponent(idx, { component_name: e.target.value })}
                      className="flex-1 px-3 py-2 border border-cream-300 rounded-xl text-sm bg-white focus:outline-none focus:border-taupe-400"
                    />
                  )}
                  {(() => {
                    const compProd = c.component_product_id ? productById.get(c.component_product_id) : undefined;
                    const isWeight = (compProd?.dimension ?? 'count') === 'weight';
                    const unit = (compProd?.display_unit as Unit) || 'unit';
                    return (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <input
                          type="number"
                          min="0"
                          step={isWeight ? '0.01' : '1'}
                          value={c.quantity}
                          onChange={(e) => updateComponent(idx, { quantity: parseFloat(e.target.value) || 0 })}
                          className="w-20 px-2 py-2 border border-cream-300 rounded-xl text-sm text-center bg-white focus:outline-none focus:border-taupe-400"
                        />
                        {compProd && (
                          <span className="text-xs text-taupe-500 w-5">{UNIT_LABEL_SHORT[unit]}</span>
                        )}
                      </div>
                    );
                  })()}
                  <button
                    onClick={() => removeComponentRow(idx)}
                    className="p-2 text-taupe-400 hover:text-red-500"
                    title="Quitar"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addComponentRow}
              className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Agregar ítem
            </button>
          </div>

          {/* Precio promo (único) */}
          <div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-taupe-700 mb-1.5">Precio promocional *</label>
                <CurrencyInput
                  value={form.price_cash}
                  onChange={(v) => updateForm({ price_cash: v })}
                />
              </div>
            </div>
            <p className="text-xs text-taupe-400 mt-1">
              Precio único del combo. El descuento por efectivo se aplica en la venta.
            </p>
          </div>

          {/* Comparación: precio normal vs precio del combo */}
          {comboNormalPrice > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-taupe-600">Precio normal (productos por separado):</span>
                <span className="font-semibold text-taupe-500 line-through">{formatPrice(comboNormalPrice)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-taupe-700 font-medium">Precio del combo:</span>
                <span className="font-bold text-purple-700">{formatPrice(cashPriceNum)}</span>
              </div>
              {cashPriceNum > 0 && comboSaving > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-taupe-600">Ahorro para el cliente:</span>
                  <span className="font-bold text-green-700">
                    {formatPrice(comboSaving)} <span className="font-normal text-taupe-400">({comboSavingPct.toFixed(0)}%)</span>
                  </span>
                </div>
              )}
              {cashPriceNum > comboNormalPrice && (
                <p className="text-xs text-amber-600">
                  El precio del combo es mayor que comprar los productos por separado.
                </p>
              )}
            </div>
          )}

          {/* Costo y ganancia */}
          {comboCost > 0 && (
            <div className="bg-cream-50 border border-cream-200 rounded-xl p-4 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-taupe-600">Costo del combo {hasComponentWithoutCost && '(parcial)'}:</span>
                <span className="font-semibold text-taupe-800">{formatPrice(comboCost)}</span>
              </div>
              {cashPriceNum > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-taupe-600">Ganancia (efectivo):</span>
                    <span className={`font-semibold ${comboProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPrice(comboProfit)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-taupe-600">% de ganancia:</span>
                    <span className={`font-bold ${comboProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {comboMarkupPct.toFixed(0)}% sobre costo
                      <span className="text-taupe-400 font-normal"> · {comboMarginPct.toFixed(0)}% margen</span>
                    </span>
                  </div>
                </>
              )}
              {hasComponentWithoutCost && (
                <p className="text-xs text-amber-600">
                  Algún producto no tiene costo cargado: el costo mostrado es parcial.
                </p>
              )}
            </div>
          )}

          {/* Stock */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Stock del combo"
              type="number"
              min="0"
              value={form.stock}
              onChange={(e) => updateForm({ stock: e.target.value })}
              disabled={!form.track_stock}
            />
            <Input
              label="Stock mínimo"
              type="number"
              min="0"
              value={form.min_stock}
              onChange={(e) => updateForm({ min_stock: e.target.value })}
              disabled={!form.track_stock}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-taupe-700">
            <input
              type="checkbox"
              checked={form.track_stock}
              onChange={(e) => updateForm({ track_stock: e.target.checked })}
              className="rounded border-cream-300"
            />
            Controlar stock de este combo
          </label>
          <p className="text-xs text-taupe-400 -mt-1">
            Al vender el combo se descuenta el stock de los productos que lo componen.
            {form.track_stock && ' Además se descuenta el stock propio del combo (cargalo arriba).'}
          </p>

          {/* Acciones */}
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              disabled={!isValid || createProduct.isPending || updateProduct.isPending}
              onClick={handleSave}
            >
              <Check className="h-5 w-5 mr-2" />
              {editing ? 'Guardar cambios' : 'Crear combo'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal eliminar */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Eliminar combo">
        <div className="space-y-4">
          <p>
            ¿Eliminar el combo <strong>{deleteConfirm?.name}</strong>?
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setDeleteConfirm(null)}>
              No
            </Button>
            <Button variant="danger" className="flex-1" onClick={handleDelete} disabled={deleteProduct.isPending}>
              Sí, eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
